import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  BookmarkSquareIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  CheckBadgeIcon,
  DocumentCheckIcon,
  InformationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon as CheckCircleSolid,
  BookmarkSquareIcon as BookmarkSquareSolid 
} from '@heroicons/react/24/solid';
import { StudentSession } from '../../types';
import { ExamScheduleConfig, ExamAttemptRecord, ExamPortalPhase } from '../../types/exam';
import { 
  getActiveExamConfig, 
  getExamPortalPhase, 
  recordCandidateExamLogin, 
  getCandidateExamAttempt, 
  saveExamProgress, 
  submitCandidateExam,
  syncExamProgressToServer,
  formatIST,
  getAttemptId,
} from '../../services/examService';
import { getOrAssignCandidateQuestions } from '../../utils/examRandomizer';

interface ExamPortalProps {
  session: StudentSession;
  onLogout: () => void;
}

const ExamPortal: React.FC<ExamPortalProps> = ({ session, onLogout }) => {
  const [examConfig, setExamConfig] = useState<ExamScheduleConfig>(getActiveExamConfig());
  const [attempt, setAttempt] = useState<ExamAttemptRecord | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [reviewFlags, setReviewFlags] = useState<number[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [portalPhase, setPortalPhase] = useState<ExamPortalPhase>('LOBBY_OPEN');
  
  // Real-time proctoring and anti-cheating state
  const [antiCheatWarning, setAntiCheatWarning] = useState<string | null>(null);
  const [tabSwitches, setTabSwitches] = useState<number>(0);
  const [copyPastes, setCopyPastes] = useState<number>(0);
  
  // Network connectivity and sync states
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<'saved' | 'saving' | 'offline_saved'>('saved');

  // Assign 30 randomized questions for this candidate from the 50-question bank
  const { questions: candidateQuestions } = useMemo(() => {
    return getOrAssignCandidateQuestions(
      examConfig.questions,
      attempt?.assignedQuestionIds,
      session.registrationNumber || session.regNoSuffix,
      examConfig.totalQuestions || 30
    );
  }, [examConfig.questions, attempt?.assignedQuestionIds, session, examConfig.totalQuestions]);

  // Real-time timers
  const [secondsUntilExamStart, setSecondsUntilExamStart] = useState<number>(0);
  const [examRemainingSeconds, setExamRemainingSeconds] = useState<number>(45 * 60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  // References to keep timer decoupled from re-renders and answer selection
  const attemptRef = useRef<ExamAttemptRecord | null>(attempt);
  attemptRef.current = attempt;
  const isSubmittingRef = useRef<boolean>(isSubmitting);
  isSubmittingRef.current = isSubmitting;
  const candidateQuestionsRef = useRef(candidateQuestions);
  candidateQuestionsRef.current = candidateQuestions;
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const examConfigRef = useRef(examConfig);
  examConfigRef.current = examConfig;

  // Monitor network online/offline transitions & auto-sync when connection restores
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      if (attempt) {
        setSavingStatus('saving');
        const ok = await syncExamProgressToServer(attempt);
        if (ok) {
          setSavingStatus('saved');
          setSyncNotice('Network reconnected • All answers verified and synchronized with server.');
          setTimeout(() => setSyncNotice(null), 5000);
        }
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setSavingStatus('offline_saved');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [attempt]);

  // Periodic background sync: every 2 minutes (120s), ensures Firestore has latest state while conserving quota
  useEffect(() => {
    const syncInterval = setInterval(async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine && attempt && attempt.status !== 'submitted') {
        await syncExamProgressToServer(attempt);
      }
    }, 120000);

    return () => clearInterval(syncInterval);
  }, [attempt]);

  // Initialize or fetch attempt with bidirectional reconciliation between local cache and Firestore
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      const cfg = getActiveExamConfig();
      setExamConfig(cfg);

      const existing = await getCandidateExamAttempt(
        cfg.id, 
        session.registrationNumber || session.regNoSuffix,
        session.regNoSuffix
      );

      // Self-heal: If attempt was prematurely auto-submitted with 0 answers, discard and start fresh!
      const answeredCount = Object.keys(existing?.answers || {}).length;
      const isPrematureZeroSubmit = existing && existing.status === 'submitted' && answeredCount === 0;

      if (existing && !isPrematureZeroSubmit) {
        if (isMounted) {
          const activeAtt: ExamAttemptRecord = {
            ...existing,
            status: existing.status === 'submitted' ? 'submitted' : 'in_progress',
            startTimestamp: existing.startTimestamp || Date.now(),
          };
          setAttempt(activeAtt);
          setAnswers(existing.answers || {});
          setReviewFlags(existing.reviewFlags || []);
          setTabSwitches(existing.tabSwitchCount || 0);
          setCopyPastes(existing.copyPasteAttempts || 0);
        }
      } else {
        if (isPrematureZeroSubmit) {
          const attId = getAttemptId(cfg.id, session.registrationNumber || session.regNoSuffix);
          try {
            localStorage.removeItem(`kreed_exam_attempt_${attId}`);
          } catch {}
        }
        const created = await recordCandidateExamLogin(session, cfg.id);
        if (isMounted) {
          const activeAtt = {
            ...created,
            status: 'in_progress' as const,
            startTimestamp: Date.now(),
          };
          setAttempt(activeAtt);
          setAnswers(created.answers || {});
          setReviewFlags(created.reviewFlags || []);
          setTabSwitches(0);
          setCopyPastes(0);
        }
      }
    };

    init();
    return () => { isMounted = false; };
  }, [session]);

  const handleFinalSubmit = useCallback(async () => {
    if (!attempt || isSubmitting) return;
    setIsSubmitting(true);
    setShowConfirmSubmit(false);

    // Instant optimistic submission transition: UI moves immediately to success screen
    const immediateCompleted: ExamAttemptRecord = {
      ...attempt,
      answers,
      status: 'submitted',
      submissionTime: formatIST(new Date()),
      submissionTimestamp: Date.now(),
      lastSavedAt: Date.now(),
    };
    setAttempt(immediateCompleted);

    try {
      const finalized = await submitCandidateExam(attempt, answers, examConfig, candidateQuestions);
      setAttempt(finalized);
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [attempt, answers, examConfig, candidateQuestions, isSubmitting]);

  // Tick clock and run independent 45-minute examination countdown
  useEffect(() => {
    const reg = session.registrationNumber || session.regNoSuffix || 'default';
    const storageKey = `kreed_exam_starttime_${reg}`;
    let startTime: number;
    try {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      if (!isNaN(parsed) && parsed > 0 && Date.now() - parsed < 8 * 60 * 60 * 1000) {
        startTime = parsed;
      } else {
        startTime = Date.now();
        localStorage.setItem(storageKey, String(startTime));
      }
    } catch {
      startTime = Date.now();
    }

    const phaseNow = getExamPortalPhase().phase;
    if (phaseNow === 'EXAM_ENDED') {
      setPortalPhase('EXAM_ENDED');
      return;
    }

    setPortalPhase('EXAM_ACTIVE');
    setSecondsUntilExamStart(0);

    const updateTimerTick = () => {
      const cfg = getActiveExamConfig();
      const totalAllowedSeconds = (cfg.durationMinutes || 45) * 60;
      const elapsed = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const remaining = Math.max(0, totalAllowedSeconds - elapsed);
      setExamRemainingSeconds(remaining);

      // Auto-submit ONLY when countdown reaches 0 and test is active
      if (
        remaining <= 0 &&
        attemptRef.current &&
        attemptRef.current.status === 'in_progress' &&
        !isSubmittingRef.current
      ) {
        handleFinalSubmit();
      }
    };

    updateTimerTick();
    const interval = setInterval(updateTimerTick, 1000);
    return () => clearInterval(interval);
  }, [session, handleFinalSubmit]);

  // Real-time Anti-Cheating & Proctoring Event Listeners
  useEffect(() => {
    if (!attempt || attempt.status === 'submitted') return;

    let warningTimeout: any;
    const showWarning = (msg: string) => {
      setAntiCheatWarning(msg);
      clearTimeout(warningTimeout);
      warningTimeout = setTimeout(() => setAntiCheatWarning(null), 7000);
    };

    const handleVisibility = () => {
      if (document.hidden && attempt.status !== 'submitted') {
        setTabSwitches(prev => {
          const next = prev + 1;
          showWarning(`⚠️ Proctoring Alert (${next}): Tab switch detected! Leaving the exam tab is recorded and flagged for AI / cheating review.`);
          const updatedTelemetry: Partial<ExamAttemptRecord> = {
            tabSwitchCount: next,
            aiSuspicionLevel: next >= 3 ? 'HIGH_RISK_AI_SUSPECTED' : 'SUSPICIOUS',
            aiSuspicionScore: Math.min(100, 30 + next * 20),
            lastSavedAt: Date.now(),
            isLiveActive: true,
          };
          setAttempt(curr => curr ? { ...curr, ...updatedTelemetry } : null);
          saveExamProgress({ ...attempt, ...updatedTelemetry } as ExamAttemptRecord, answers, reviewFlags, attempt.status).catch(() => {});
          return next;
        });
      }
    };

    const handleBlur = () => {
      if (attempt.status !== 'submitted') {
        showWarning('⚠️ Exam window lost focus. Please remain exclusively on the test screen.');
      }
    };

    const handleCopyCut = (e: ClipboardEvent) => {
      e.preventDefault();
      setCopyPastes(prev => {
        const next = prev + 1;
        showWarning(`⚠️ Clipboard Restricted: Copying question text or pasting content is strictly forbidden.`);
        const updatedTelemetry: Partial<ExamAttemptRecord> = {
          copyPasteAttempts: next,
          aiSuspicionLevel: next >= 2 ? 'HIGH_RISK_AI_SUSPECTED' : 'SUSPICIOUS',
          lastSavedAt: Date.now(),
          isLiveActive: true,
        };
        setAttempt(curr => curr ? { ...curr, ...updatedTelemetry } : null);
        saveExamProgress({ ...attempt, ...updatedTelemetry } as ExamAttemptRecord, answers, reviewFlags, attempt.status).catch(() => {});
        return next;
      });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('copy', handleCopyCut);
    document.addEventListener('cut', handleCopyCut);
    document.addEventListener('paste', handleCopyCut);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('copy', handleCopyCut);
      document.removeEventListener('cut', handleCopyCut);
      document.removeEventListener('paste', handleCopyCut);
      clearTimeout(warningTimeout);
    };
  }, [attempt, answers, reviewFlags]);

  // Immediate local cache write + debounced server sync whenever answers or flags change
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const triggerAutoSave = useCallback((newAnswers: Record<number, number>, newFlags: number[]) => {
    if (!attempt || attempt.status === 'submitted') return;

    // 1. Immediately cache synchronously into localStorage so data is NEVER lost even if tab crashes/network cuts
    try {
      const cacheKey = `kreed_exam_attempt_${attempt.attemptId}`;
      const fastCached: ExamAttemptRecord = {
        ...attempt,
        answers: newAnswers,
        reviewFlags: newFlags,
        lastSavedAt: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(fastCached));
    } catch (e) {
      // ignore
    }

    // 2. Debounced push to Firestore server (conserves database writes while keeping server progress fresh)
    setSavingStatus(typeof navigator !== 'undefined' && navigator.onLine ? 'saving' : 'offline_saved');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      try {
        const updated = await saveExamProgress(attempt, newAnswers, newFlags, 'in_progress');
        setAttempt(updated);
        setSavingStatus(typeof navigator !== 'undefined' && navigator.onLine ? 'saved' : 'offline_saved');
      } catch (e) {
        setSavingStatus('offline_saved');
      }
    }, 2500);
  }, [attempt]);

  const handleSelectOption = (questionId: number, optionIdx: number) => {
    if (attempt?.status === 'submitted') return;
    const nextAnswers = { ...answers, [questionId]: optionIdx };
    setAnswers(nextAnswers);
    triggerAutoSave(nextAnswers, reviewFlags);
  };

  const handleClearAnswer = (questionId: number) => {
    if (attempt?.status === 'submitted') return;
    const nextAnswers = { ...answers };
    delete nextAnswers[questionId];
    setAnswers(nextAnswers);
    triggerAutoSave(nextAnswers, reviewFlags);
  };

  const handleToggleReviewFlag = (questionId: number) => {
    if (attempt?.status === 'submitted') return;
    let nextFlags: number[];
    if (reviewFlags.includes(questionId)) {
      nextFlags = reviewFlags.filter(id => id !== questionId);
    } else {
      nextFlags = [...reviewFlags, questionId];
    }
    setReviewFlags(nextFlags);
    triggerAutoSave(answers, nextFlags);
  };

  // Format seconds to MM:SS
  const formatTimer = (totalSeconds: number): string => {
    const mins = Math.floor(Math.max(0, totalSeconds) / 60);
    const secs = Math.floor(Math.max(0, totalSeconds) % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Summary counts
  const totalQuestions = candidateQuestions.length || examConfig.totalQuestions || 30;
  const answeredCount = Object.keys(answers).length;
  const markedReviewCount = reviewFlags.length;
  const unattemptedCount = Math.max(0, totalQuestions - answeredCount);

  const currentQ = candidateQuestions[currentQuestionIndex] || candidateQuestions[0];

  const effectivePhase: ExamPortalPhase = portalPhase === 'LOCKED_BEFORE_LOGIN' ? 'LOBBY_OPEN' : portalPhase;

  const renderDemoToolbar = () => null;

  // ==========================================
  // STAGE 3: TEST COMPLETED / SUBMITTED SCREEN
  // Realtime scorecard is restricted exclusively to Admin console
  // Students only receive official submission acknowledgement
  // ==========================================
  if (attempt?.status === 'submitted' || portalPhase === 'EXAM_ENDED') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between">
        {renderDemoToolbar()}
        <div className="p-4 sm:p-8 flex-1 flex flex-col justify-between">
        {/* Header */}
        <header className="max-w-4xl mx-auto w-full flex items-center justify-between border-b border-white/10 pb-4 mb-6">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo192.png" 
              alt="Kreed Odisha Logo" 
              className="w-10 h-10 sm:w-11 sm:h-11 object-contain drop-shadow" 
            />
            <div>
              <h1 className="text-base sm:text-lg font-black tracking-tight text-white uppercase">
                Kreed Odisha Examination System
              </h1>
              <p className="text-xs text-amber-400 font-semibold">{examConfig.title}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-rose-500/20 hover:text-rose-300 text-xs font-semibold transition border border-white/10 cursor-pointer"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </header>

        {/* Submission Confirmation Hero */}
        <main className="max-w-3xl mx-auto w-full space-y-6">
          <div className="bg-gradient-to-br from-slate-900 via-slate-900/90 to-emerald-950/60 border border-emerald-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center shadow-lg">
              <CheckBadgeIcon className="w-10 h-10" />
            </div>

            <span className="px-3.5 py-1 rounded-full text-xs font-black tracking-wider bg-emerald-500 text-slate-950 uppercase shadow-md inline-block mb-3">
              EXAMINATION SUBMITTED SUCCESSFULLY
            </span>

            <h2 className="text-2xl sm:text-3xl font-black text-white">
              Official Examination Submission Acknowledgement
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 mt-2 max-w-lg mx-auto leading-relaxed">
              Your test responses have been encrypted, verified, and officially recorded into the central Kreed Odisha examination evaluation registry.
            </p>

            {/* Candidate Submission Metrics & Marks */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 my-6 text-left">
              <div className="bg-gradient-to-br from-emerald-950/60 to-black/60 border border-emerald-500/40 rounded-2xl p-4">
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider block">Total Marks</span>
                <span className="text-2xl sm:text-3xl font-black text-white mt-1 block">
                  {attempt?.score ?? 0} <span className="text-xs text-slate-400 font-normal">/ {totalQuestions}</span>
                </span>
                <span className="text-[10px] text-emerald-400/90 mt-0.5 block font-semibold">{attempt?.percentage ?? 0}% Score</span>
              </div>

              <div className="bg-gradient-to-br from-blue-950/60 to-black/60 border border-blue-500/40 rounded-2xl p-4">
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider block">Correct Answers</span>
                <span className="text-2xl sm:text-3xl font-black text-emerald-400 mt-1 block">
                  {attempt?.correctCount ?? 0} <span className="text-xs text-slate-400 font-normal">/ {totalQuestions}</span>
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5 block font-medium">Valid Responses</span>
              </div>

              <div className="bg-gradient-to-br from-rose-950/60 to-black/60 border border-rose-500/40 rounded-2xl p-4">
                <span className="text-[10px] font-bold text-rose-400 uppercase tracking-wider block">Incorrect / Skipped</span>
                <span className="text-xl sm:text-2xl font-black text-rose-300 mt-1 block">
                  {attempt?.wrongCount ?? 0} <span className="text-xs text-slate-400 font-normal">wrong</span>
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5 block">{attempt?.unattemptedCount ?? 0} unattempted</span>
              </div>

              <div className="bg-black/40 border border-white/10 rounded-2xl p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Submission Status</span>
                <span className="text-sm sm:text-base font-black text-emerald-400 mt-1 flex items-center space-x-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>RECORDED</span>
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5 block">Locked on server</span>
              </div>
            </div>

            {/* Candidate Metadata Summary */}
            <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-xs text-slate-300 flex flex-wrap items-center justify-around gap-3 text-left">
              <div>
                <span className="text-[10px] text-slate-400 block uppercase">Candidate Name</span>
                <strong className="text-white text-sm">{session.studentName}</strong>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 block uppercase">Registration ID</span>
                <strong className="text-amber-300 text-sm">{session.registrationNumber || session.regNoSuffix}</strong>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 block uppercase">Lobby Join Time</span>
                <span className="text-white font-medium">{attempt?.loginTime || 'Logged'}</span>
              </div>
              <div>
                <span className="text-[10px] text-slate-400 block uppercase">Submission Time</span>
                <span className="text-emerald-300 font-medium">{attempt?.submissionTime || formatIST()}</span>
              </div>
            </div>

            {/* Official Notice */}
            <div className="mt-5 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200/90 text-left flex items-start space-x-2.5">
              <InformationCircleIcon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <strong className="text-amber-300 block font-bold">Important Notice Regarding Results:</strong>
                <p className="text-[11px] text-amber-100/80 leading-relaxed">
                  As per official examination guidelines, real-time scorecards and performance analytics are supervised exclusively by authorized examination administrators. Final evaluation and merit lists will be published following standard verification procedures.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={async () => {
                  const reg = session.registrationNumber || session.regNoSuffix;
                  const attId = getAttemptId(examConfig.id, reg);
                  try {
                    localStorage.removeItem(`kreed_exam_attempt_${attId}`);
                    localStorage.removeItem(`kreed_exam_starttime_${reg}`);
                  } catch {}
                  const created = await recordCandidateExamLogin(session, examConfig.id);
                  setAttempt({
                    ...created,
                    status: 'in_progress',
                    startTimestamp: Date.now(),
                  });
                  setAnswers({});
                  setReviewFlags([]);
                  setCurrentQuestionIndex(0);
                }}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-400 hover:to-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider transition shadow-lg cursor-pointer flex items-center justify-center space-x-2"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>Enter / Retake Live Examination Now</span>
              </button>
              <button
                onClick={onLogout}
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-white font-black text-xs uppercase tracking-wider transition shadow-lg cursor-pointer flex items-center justify-center space-x-2"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                <span>Exit Portal &amp; Sign Out</span>
              </button>
            </div>
          </div>
        </main>

        <footer className="text-center text-xs text-slate-500 py-4">
          Government of Odisha • Sports &amp; Youth Services Department • Official Examination Protocol
        </footer>
        </div>
      </div>
    );
  }

  // ==========================================
  // STAGE 1: EXAMINATION LOBBY (WAITING HALL)
  // ==========================================
  if (effectivePhase === 'LOBBY_OPEN' || effectivePhase === 'LOGIN_CLOSED') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between">
        {renderDemoToolbar()}
        <div className="p-4 sm:p-8 flex-1 flex flex-col justify-between">
        {/* Top bar */}
        <header className="max-w-4xl mx-auto w-full flex items-center justify-between border-b border-white/10 pb-4 mb-6">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo192.png" 
              alt="Kreed Odisha Logo" 
              className="w-10 h-10 sm:w-11 sm:h-11 object-contain drop-shadow" 
            />
            <div>
              <h1 className="text-base sm:text-lg font-black tracking-tight text-white uppercase">
                Official Examination Lobby
              </h1>
              <p className="text-xs text-amber-400 font-semibold">{examConfig.title}</p>
            </div>
          </div>

          <button
            onClick={onLogout}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-rose-500/20 hover:text-rose-300 text-xs font-semibold transition border border-white/10"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </header>

        {/* Main Lobby Content */}
        <main className="max-w-3xl mx-auto w-full space-y-6">
          {/* Waiting Banner */}
          <div className="bg-gradient-to-br from-amber-950/50 via-slate-900 to-orange-950/40 border border-amber-500/40 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl">
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="flex flex-col items-center text-center space-y-4">
              <span className="px-4 py-1.5 rounded-full text-xs font-black tracking-wider bg-amber-500 text-slate-950 uppercase shadow-lg flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-950 animate-ping" />
                <span>EXAMINATION LOBBY ACTIVE</span>
              </span>

              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
                Welcome to the Examination Hall
              </h2>
              <p className="text-xs sm:text-sm text-amber-100/90 max-w-lg leading-relaxed">
                You are securely logged into the examination waiting lobby. The test questions will automatically unlock at <strong className="text-white">{examConfig.examStartTime} IST sharp</strong>. Please stay on this screen.
              </p>

              {/* Big Countdown Clock */}
              <div className="bg-black/60 border border-amber-500/40 rounded-2xl px-6 py-4 my-2 shadow-inner inline-flex flex-col items-center">
                <span className="text-[11px] font-bold text-amber-300 uppercase tracking-widest mb-1 flex items-center space-x-1.5">
                  <ClockIcon className="w-4 h-4" />
                  <span>Test Commences In</span>
                </span>
                <span className="text-4xl sm:text-5xl font-black tracking-tight text-white font-mono">
                  {formatTimer(secondsUntilExamStart)}
                </span>
                <span className="text-[10px] text-slate-400 mt-1">
                  Automatic countdown to {examConfig.examStartTime} IST
                </span>
              </div>

              {/* Candidate Info Badge */}
              <div className="w-full pt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-amber-200">
                <span className="bg-black/50 px-3.5 py-1.5 rounded-xl border border-white/10 flex items-center space-x-1.5">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-400" />
                  <span>Candidate: <strong className="text-white">{session.studentName}</strong></span>
                </span>
                <span className="bg-black/50 px-3.5 py-1.5 rounded-xl border border-white/10">
                  Reg ID: <strong className="text-amber-300">{session.registrationNumber || session.regNoSuffix}</strong>
                </span>
                <span className="bg-black/50 px-3.5 py-1.5 rounded-xl border border-white/10">
                  Mobile: <strong className="text-white">{session.mobileNumber}</strong>
                </span>
                <span className="bg-black/50 px-3.5 py-1.5 rounded-xl border border-white/10">
                  Batch: <strong className="text-white">Unified Examination Batch</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Exam Rules & Guidelines */}
          <div className="bg-slate-900/80 border border-white/10 rounded-3xl p-6 sm:p-8 backdrop-blur-md space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center space-x-2">
              <InformationCircleIcon className="w-5 h-5 text-amber-400" />
              <span>Official Candidate Instructions &amp; Examination Protocol</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-300">
              <div className="flex items-start space-x-2.5 bg-black/30 p-3 rounded-xl border border-white/5">
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold shrink-0">1</span>
                <div>
                  <strong className="text-white block">30 Multiple Choice Questions (MCQs)</strong>
                  Each question has 4 choices with exactly one correct option based on your official curriculum.
                </div>
              </div>

              <div className="flex items-start space-x-2.5 bg-black/30 p-3 rounded-xl border border-white/5">
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold shrink-0">2</span>
                <div>
                  <strong className="text-white block">45 Minutes Total Duration</strong>
                  The examination runs strictly from {examConfig.examStartTime} to {examConfig.examEndTime} IST. A live timer will be displayed.
                </div>
              </div>

              <div className="flex items-start space-x-2.5 bg-black/30 p-3 rounded-xl border border-white/5">
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold shrink-0">3</span>
                <div>
                  <strong className="text-white block">No Negative Marking</strong>
                  1 Mark awarded for every correct answer. Zero marks for incorrect or skipped answers.
                </div>
              </div>

              <div className="flex items-start space-x-2.5 bg-black/30 p-3 rounded-xl border border-white/5">
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold shrink-0">4</span>
                <div>
                  <strong className="text-white block">Instant Autosave Protection</strong>
                  Every option selected is automatically synchronized to cloud storage. You can navigate freely.
                </div>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200/90 flex items-center space-x-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 shrink-0" />
              <span>
                <strong>Important:</strong> Do not close or refresh this tab. Once {examConfig.examStartTime} IST arrives, the question paper will start automatically.
              </span>
            </div>
          </div>
        </main>

        <footer className="text-center text-xs text-slate-500 py-4">
          Government of Odisha • Sports &amp; Youth Services Department • Kreed Odisha Excellence Program
        </footer>
        </div>
      </div>
    );
  }

  // ==========================================
  // STAGE 2: LIVE EXAMINATION IN PROGRESS
  // (8:15 PM to 9:00 PM)
  // ==========================================
  const isTimeCritical = examRemainingSeconds < 5 * 60; // Less than 5 mins left

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between">
      {renderDemoToolbar()}
      {/* Sticky Header with Timer & Actions */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-xl border-b border-white/10 px-4 sm:px-8 py-3.5 shadow-xl">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          {/* Left: Candidate info */}
          <div className="flex items-center space-x-3">
            <img 
              src="/logo192.png" 
              alt="Kreed Odisha Logo" 
              className="w-10 h-10 sm:w-11 sm:h-11 object-contain drop-shadow shrink-0" 
            />
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm font-black text-white uppercase tracking-tight">
                  {examConfig.title}
                </span>
                <span className="hidden sm:inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  LIVE
                </span>
              </div>
              <div className="flex items-center space-x-2 text-[10px] text-slate-400">
                <span>Candidate: <strong className="text-slate-200">{session.studentName}</strong> ({session.registrationNumber || session.regNoSuffix})</span>
                {tabSwitches > 0 && (
                  <span className="text-rose-400 font-bold bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/30">
                    ⚠️ {tabSwitches} Focus Alert{tabSwitches > 1 ? 's' : ''}
                  </span>
                )}
                {copyPastes > 0 && (
                  <span className="text-amber-400 font-bold bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/30">
                    ⚠️ Clipboard Warning
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Center/Right: Timer, Network & Autosave status */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="hidden sm:flex items-center space-x-1.5 text-xs">
              {!isOnline ? (
                <span className="flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/40 font-medium">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  <span>Offline (Saved in Local Cache)</span>
                </span>
              ) : savingStatus === 'saving' ? (
                <span className="flex items-center space-x-1 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping shrink-0" />
                  <span>Syncing to Server...</span>
                </span>
              ) : (
                <span className="flex items-center space-x-1 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                  <span>Cloud Synced</span>
                </span>
              )}
            </div>

            {/* Countdown Badge */}
            <div className={`px-4 py-2 rounded-2xl border flex items-center space-x-2 font-mono font-black text-base sm:text-lg shadow-lg ${
              isTimeCritical 
                ? 'bg-rose-950/70 border-rose-500/60 text-rose-300 animate-pulse' 
                : 'bg-black/60 border-amber-500/40 text-amber-300'
            }`}>
              <ClockIcon className="w-5 h-5 shrink-0" />
              <span>{formatTimer(examRemainingSeconds)}</span>
            </div>

            {/* Submit Button */}
            <button
              onClick={() => setShowConfirmSubmit(true)}
              className="px-4 sm:px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-lg transition"
            >
              Finish &amp; Submit
            </button>
          </div>
        </div>
      </header>

      {/* Real-time Anti-Cheating & Proctoring Warning Alert */}
      {antiCheatWarning && (
        <div className="bg-gradient-to-r from-rose-900 via-rose-950 to-rose-900 border-b border-rose-500/70 px-4 py-3 text-xs sm:text-sm text-rose-100 flex items-center justify-between shadow-2xl animate-bounce">
          <div className="flex items-center space-x-2.5 max-w-4xl mx-auto">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 shrink-0 animate-pulse" />
            <span className="font-semibold">{antiCheatWarning}</span>
          </div>
          <button 
            onClick={() => setAntiCheatWarning(null)}
            className="text-white/70 hover:text-white px-2 py-1 rounded bg-black/30 text-xs font-bold shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Offline Notice Banner */}
      {!isOnline && (
        <div className="bg-gradient-to-r from-amber-950 via-amber-900 to-amber-950 border-b border-amber-500/50 px-4 py-2.5 text-xs text-amber-200 flex flex-wrap items-center justify-between gap-2 shadow-lg">
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping shrink-0" />
            <span>
              <strong>Network Disconnected (Offline Protection Active):</strong> You can continue answering questions. All your responses are automatically preserved in your local browser cache and will immediately sync with the server once connection is restored.
            </span>
          </div>
          <div className="flex items-center space-x-2 text-[11px]">
            <span className="px-2.5 py-1 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono">
              Local Cache: Active ({answeredCount} answered)
            </span>
          </div>
        </div>
      )}

      {/* Online Sync Notice Banner */}
      {syncNotice && (
        <div className="bg-emerald-950/90 border-b border-emerald-500/50 px-4 py-2 text-xs text-emerald-200 flex items-center justify-center space-x-2 animate-fade-in shadow-lg">
          <CheckBadgeIcon className="w-4 h-4 text-emerald-400" />
          <span>{syncNotice}</span>
        </div>
      )}

      {/* Main Examination Work Area */}
      <main className="max-w-6xl mx-auto w-full p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1">
        {/* Left Column: Current Question Card (8 cols) */}
        <section className="lg:col-span-8 flex flex-col space-y-4">
          <div className="bg-slate-900/90 border border-white/10 rounded-3xl p-6 sm:p-8 backdrop-blur-xl shadow-2xl flex-1 flex flex-col justify-between">
            <div>
              {/* Question Meta Header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-5">
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 rounded-xl text-xs font-black bg-amber-500 text-slate-950 uppercase shadow-md">
                    Question {currentQuestionIndex + 1} of {totalQuestions}
                  </span>
                  <span className="px-3 py-1 rounded-xl text-xs font-medium bg-white/10 text-slate-300 border border-white/10">
                    {currentQ.category}
                  </span>
                </div>

                <button
                  onClick={() => handleToggleReviewFlag(currentQ.id)}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition border ${
                    reviewFlags.includes(currentQ.id)
                      ? 'bg-purple-900/50 border-purple-400 text-purple-300'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                  }`}
                >
                  {reviewFlags.includes(currentQ.id) ? (
                    <BookmarkSquareSolid className="w-4 h-4 text-purple-400" />
                  ) : (
                    <BookmarkSquareIcon className="w-4 h-4" />
                  )}
                  <span>{reviewFlags.includes(currentQ.id) ? 'Marked for Review' : 'Mark for Review'}</span>
                </button>
              </div>

              {/* Question Text */}
              <h2 className="text-base sm:text-xl font-bold text-white leading-relaxed mb-6">
                {currentQ.text}
              </h2>

              {/* 4 Options */}
              <div className="space-y-3">
                {currentQ.options.map((optionText, optIdx) => {
                  const isSelected = answers[currentQ.id] === optIdx;

                  return (
                    <label
                      key={optIdx}
                      onClick={() => handleSelectOption(currentQ.id, optIdx)}
                      className={`w-full p-4 rounded-2xl border flex items-center space-x-3.5 cursor-pointer transition select-none ${
                        isSelected
                          ? 'bg-emerald-500/15 border-emerald-500 text-white shadow-lg ring-1 ring-emerald-500/50'
                          : 'bg-black/30 border-white/10 hover:border-white/25 text-slate-200 hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shrink-0 transition ${
                        isSelected
                          ? 'bg-emerald-500 text-slate-950 ring-2 ring-emerald-300'
                          : 'bg-white/10 text-slate-400'
                      }`}>
                        {String.fromCharCode(65 + optIdx)}
                      </div>
                      <span className="text-sm font-medium flex-1">{optionText}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Bottom Question Controls */}
            <div className="pt-6 mt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <button
                  disabled={currentQuestionIndex === 0}
                  onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                  className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-bold text-white uppercase tracking-wider flex items-center space-x-1.5 transition border border-white/10"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                {answers[currentQ.id] !== undefined && (
                  <button
                    onClick={() => handleClearAnswer(currentQ.id)}
                    className="px-3 py-2.5 rounded-xl text-xs font-semibold text-rose-400 hover:bg-rose-500/15 transition"
                  >
                    Clear Response
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {currentQuestionIndex < totalQuestions - 1 ? (
                  <button
                    onClick={() => setCurrentQuestionIndex(prev => Math.min(totalQuestions - 1, prev + 1))}
                    className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg flex items-center space-x-1.5 transition"
                  >
                    <span>Save &amp; Next</span>
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setShowConfirmSubmit(true)}
                    className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg flex items-center space-x-1.5 transition"
                  >
                    <CheckCircleSolid className="w-4 h-4" />
                    <span>Review &amp; Finish</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Right Column: Question Palette & Navigation (4 cols) */}
        <aside className="lg:col-span-4 flex flex-col space-y-4">
          <div className="bg-slate-900/90 border border-white/10 rounded-3xl p-5 sm:p-6 backdrop-blur-xl shadow-2xl flex-1 flex flex-col">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-300 mb-3 flex items-center justify-between">
              <span>Question Palette</span>
              <span className="text-amber-400">{answeredCount} / {totalQuestions} Answered</span>
            </h3>

            {/* Summary counters */}
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div className="bg-black/40 border border-emerald-500/30 rounded-xl p-2">
                <span className="text-base font-black text-emerald-400 block">{answeredCount}</span>
                <span className="text-[10px] text-slate-400 uppercase font-medium">Answered</span>
              </div>
              <div className="bg-black/40 border border-purple-500/30 rounded-xl p-2">
                <span className="text-base font-black text-purple-400 block">{markedReviewCount}</span>
                <span className="text-[10px] text-slate-400 uppercase font-medium">Review</span>
              </div>
              <div className="bg-black/40 border border-white/10 rounded-xl p-2">
                <span className="text-base font-black text-slate-300 block">{unattemptedCount}</span>
                <span className="text-[10px] text-slate-400 uppercase font-medium">Left</span>
              </div>
            </div>

            {/* Question Buttons Grid (30 numbers) */}
            <div className="grid grid-cols-5 gap-2 my-auto p-2 bg-black/30 rounded-2xl border border-white/5 max-h-[360px] overflow-y-auto">
              {candidateQuestions.map((q, idx) => {
                const isCurrent = idx === currentQuestionIndex;
                const isAnswered = answers[q.id] !== undefined;
                const isReview = reviewFlags.includes(q.id);

                let btnStyle = 'bg-white/10 text-slate-300 border-white/10';
                if (isReview) {
                  btnStyle = 'bg-purple-600 text-white border-purple-400 shadow-purple-500/30';
                } else if (isAnswered) {
                  btnStyle = 'bg-emerald-600 text-white border-emerald-400 shadow-emerald-500/30';
                }

                if (isCurrent) {
                  btnStyle += ' ring-2 ring-amber-400 font-black scale-105';
                }

                return (
                  <button
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`h-10 rounded-xl border text-xs font-bold transition flex items-center justify-center relative shadow ${btnStyle}`}
                  >
                    {idx + 1}
                    {isReview && isAnswered && (
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-400" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="pt-4 border-t border-white/10 grid grid-cols-2 gap-2 text-[10px] text-slate-400">
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded bg-emerald-600 shrink-0" />
                <span>Answered</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded bg-purple-600 shrink-0" />
                <span>Marked for Review</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded bg-white/10 shrink-0" />
                <span>Not Attempted</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded ring-2 ring-amber-400 bg-black/40 shrink-0" />
                <span>Current Question</span>
              </div>
            </div>
          </div>
        </aside>
      </main>

      {/* Confirmation Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/40 flex items-center justify-center shadow-lg">
              <DocumentCheckIcon className="w-8 h-8" />
            </div>

            <h3 className="text-xl font-black text-white">
              Confirm Examination Submission
            </h3>

            <p className="text-xs text-slate-300">
              Are you sure you want to finish and submit your answers? Once submitted, your scores will be computed and responses locked.
            </p>

            <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-xs text-left space-y-1.5 text-slate-300">
              <div className="flex justify-between">
                <span>Total Questions:</span>
                <strong className="text-white">{totalQuestions}</strong>
              </div>
              <div className="flex justify-between text-emerald-400">
                <span>Answered Questions:</span>
                <strong>{answeredCount}</strong>
              </div>
              <div className="flex justify-between text-purple-300">
                <span>Marked for Review:</span>
                <strong>{markedReviewCount}</strong>
              </div>
              <div className="flex justify-between text-rose-400">
                <span>Remaining Unattempted:</span>
                <strong>{unattemptedCount}</strong>
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-2">
              <button
                disabled={isSubmitting}
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition"
              >
                Back to Test
              </button>
              <button
                disabled={isSubmitting}
                onClick={handleFinalSubmit}
                className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg transition flex items-center justify-center space-x-1.5"
              >
                {isSubmitting ? <span>Submitting...</span> : <span>Confirm Submit</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPortal;
